// Carrier session + identity storage. The signed link is the durable credential
// (bookmarkable, permanent); the session token is short-lived and stored here
// after a successful exchange. The person's self-asserted identity is cached
// long-term — the link is shared around the carrier's team, so each browser
// remembers who its person is and re-asserts it on every exchange. The network
// calls live in carrierApi.ts (the generated client); this module is storage only.
const SESSION_STORAGE_KEY = "carriers:session";
const IDENTITY_STORAGE_KEY = "carriers:identity";

export interface CarrierSession {
  sessionToken: string;
  carrierIdentifier: string;
  carrierName: string;
  userName: string;
  userEmail: string;
}

/** The person using this browser, self-asserted once and cached long-term. */
export interface CarrierUserIdentity {
  name: string;
  email: string;
}

export function getStoredSession(): CarrierSession | undefined {
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.sessionToken === "string" &&
      typeof parsed?.carrierIdentifier === "string" &&
      typeof parsed?.carrierName === "string" &&
      typeof parsed?.userName === "string" &&
      typeof parsed?.userEmail === "string"
    ) {
      return {
        sessionToken: parsed.sessionToken,
        carrierIdentifier: parsed.carrierIdentifier,
        carrierName: parsed.carrierName,
        userName: parsed.userName,
        userEmail: parsed.userEmail,
      };
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export function storeSession(session: CarrierSession): void {
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage can be unavailable (private mode, quota). The session still works
    // for this page load; the next visit just re-exchanges the bookmarked link.
  }
}

export function clearStoredSession(): void {
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Ignore — nothing actionable if removal fails.
  }
}

export function getStoredIdentity(): CarrierUserIdentity | undefined {
  try {
    const raw = window.localStorage.getItem(IDENTITY_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.name === "string" &&
      parsed.name.trim() !== "" &&
      typeof parsed?.email === "string" &&
      parsed.email.trim() !== ""
    ) {
      return { name: parsed.name, email: parsed.email };
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export function storeIdentity(identity: CarrierUserIdentity): void {
  try {
    window.localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Ignore — the person will simply be asked again next visit.
  }
}

export function clearStoredIdentity(): void {
  try {
    window.localStorage.removeItem(IDENTITY_STORAGE_KEY);
  } catch {
    // Ignore — nothing actionable if removal fails.
  }
}
